// =============================================================================
// hooks/crm/useLeadAppointments.ts — Ponte de leitura entre o CRM/Kanban e a
// agenda comercial (`contact_appointments`, F33-S02/F33-S07, doc 26 §6.2/6.3).
//
// Por que este arquivo existe (em vez de estender hooks/sales/useAppointments.ts):
//   `files_allowed` deste slot cobre `hooks/crm/**` e `hooks/kanban/**`, mas
//   NÃO `hooks/sales/**` (dono: F33-S05). Em vez de tocar um arquivo fora do
//   escopo do slot, este módulo chama a MESMA rota REST
//   (`GET /api/sales/appointments`) e o MESMO schema Zod
//   (`AppointmentListResponseSchema`) que `hooks/sales/useAppointments.ts` já
//   usa — nenhum cálculo local paralelo, nenhuma duplicação de contrato,
//   apenas um cliente HTTP adicional para o mesmo endpoint (DoD do slot:
//   "Próxima ação no CRM vem da API de compromissos, sem cálculo local
//   paralelo").
//
// Gate único: toda leitura daqui exige `sales_appointments:read` E a flag
// `sales.appointments.enabled` — ver `useAppointmentsGate()`. Com qualquer um
// dos dois ausente, os hooks abaixo ficam `enabled: false` (nenhuma
// requisição sai) e os componentes que os chamam devem renderizar `null`
// (DoD: "sem elas o CRM fica idêntico ao de hoje").
//
// `limit` do endpoint tem teto de 100 (packages/shared-schemas/sales-appointments.ts)
// — mesmo teto documentado em hooks/sales/useAppointmentLeadSummaries.ts:
// "aceitável para o volume do piloto comercial (dezenas, nunca milhares)".
// =============================================================================

import type { Appointment, AppointmentListResponse } from '@elemento/shared-schemas';
import { AppointmentListResponseSchema } from '@elemento/shared-schemas';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth-store';
import { endOfDayInOrgTz, startOfDayInOrgTz } from '../../lib/orgDateTime';
import { useFeatureFlag } from '../useFeatureFlag';

// ---------------------------------------------------------------------------
// Gate — permissão + flag do módulo comercial (mesma dupla de
// features/sales/agenda/AgendaPage.tsx e
// features/conversations/components/ScheduleReturnButton.tsx).
// ---------------------------------------------------------------------------

const FLAG_KEY = 'sales.appointments.enabled';
const READ_PERMISSION = 'sales_appointments:read';
const WRITE_PERMISSION = 'sales_appointments:write';

export interface AppointmentsGate {
  /** true quando o read-gate (permissão + flag) está liberado. */
  canRead: boolean;
  /** true quando, além do read-gate, o ator também pode agendar. */
  canWrite: boolean;
  /** true enquanto a flag ainda não respondeu — evita "flash" de conteúdo. */
  isLoading: boolean;
}

export function useAppointmentsGate(): AppointmentsGate {
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled, isLoading } = useFeatureFlag(FLAG_KEY);

  const canRead = hasPermission(READ_PERMISSION) && flagEnabled;
  const canWrite = canRead && hasPermission(WRITE_PERMISSION);

  return { canRead, canWrite, isLoading };
}

// ---------------------------------------------------------------------------
// Fetch cru — mesma rota/schema de hooks/sales/useAppointments.ts.
// ---------------------------------------------------------------------------

async function fetchAppointments(qs: URLSearchParams): Promise<AppointmentListResponse> {
  const query = qs.toString();
  const raw = await api.get<unknown>(`/api/sales/appointments${query ? `?${query}` : ''}`);
  return AppointmentListResponseSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// useLeadNextActions — "próxima ação" em lote, por leadId.
//
// Usada tanto pelo chip do card do Kanban (vários leadIds visíveis de uma
// vez, um único GET) quanto pela coluna equivalente da lista tabular e pelo
// card "Próxima ação" do detalhe (um leadId só). Fonte única para as três
// superfícies — doc 26 §22 "Agenda, CRM, conversa e campanha mostram a mesma
// próxima ação".
// ---------------------------------------------------------------------------

export interface UseLeadNextActionsResult {
  /** leadId → compromisso agendado mais próximo (status=scheduled, o 1º por scheduledAt asc). */
  nextActionByLead: Map<string, Appointment>;
  isLoading: boolean;
}

export function useLeadNextActions(
  leadIds: string[],
  opts: { enabled?: boolean } = {},
): UseLeadNextActionsResult {
  // Chave estável independente da ordem/duplicatas de entrada — evita refetch
  // a cada render quando o array de origem é recriado com o mesmo conteúdo.
  const leadIdsKey = leadIds.join(',');
  const sortedUniqueIds = React.useMemo(() => Array.from(new Set(leadIds)).sort(), [leadIdsKey]);

  const enabled = (opts.enabled ?? true) && sortedUniqueIds.length > 0;

  const { data, isLoading } = useQuery({
    queryKey: ['crm', 'lead-next-actions', sortedUniqueIds] as const,
    queryFn: () => {
      const qs = new URLSearchParams();
      for (const id of sortedUniqueIds) qs.append('leadId', id);
      qs.set('status', 'scheduled');
      qs.set('sortBy', 'scheduledAt');
      qs.set('sortOrder', 'asc');
      qs.set('limit', '100');
      return fetchAppointments(qs);
    },
    enabled,
    staleTime: 15_000,
  });

  const nextActionByLead = new Map<string, Appointment>();
  if (data) {
    // Ordenado asc por scheduledAt — a 1ª ocorrência por lead é a mais próxima.
    for (const appointment of data.data) {
      if (!nextActionByLead.has(appointment.leadId)) {
        nextActionByLead.set(appointment.leadId, appointment);
      }
    }
  }

  return { nextActionByLead, isLoading: enabled && isLoading };
}

// ---------------------------------------------------------------------------
// useLeadAppointmentsTimeline — TODOS os compromissos (qualquer status) de
// UM lead, para a linha do tempo única do detalhe (doc 26 §6.3).
// ---------------------------------------------------------------------------

export function useLeadAppointmentsTimeline(
  leadId: string,
  opts: { enabled?: boolean } = {},
): { appointments: Appointment[]; isLoading: boolean } {
  const enabled = (opts.enabled ?? true) && leadId.length > 0;

  const { data, isLoading } = useQuery({
    queryKey: ['crm', 'lead-appointments-timeline', leadId] as const,
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('leadId', leadId);
      qs.set('sortBy', 'scheduledAt');
      qs.set('sortOrder', 'desc');
      qs.set('limit', '50');
      return fetchAppointments(qs);
    },
    enabled,
    staleTime: 15_000,
  });

  return { appointments: data?.data ?? [], isLoading: enabled && isLoading };
}

// ---------------------------------------------------------------------------
// useDueLeadIds — leadIds com compromisso "hoje" ou "atrasado", para os
// filtros rápidos do CRM/Kanban (doc 26 §6.2).
//
// "Atrasado" nunca é recomputado por data local — vem de `isOverdue` da API
// (mesma regra de features/sales/agenda/labels.ts urgencyLevel). "Hoje" é a
// janela civil completa no fuso da organização (mesma regra de
// features/sales/agenda/buckets.ts hojeWindow).
// ---------------------------------------------------------------------------

export type DueWindow = 'today' | 'overdue';

export function useDueLeadIds(
  window: DueWindow,
  opts: { enabled?: boolean } = {},
): { leadIds: Set<string>; isLoading: boolean } {
  const enabled = opts.enabled ?? true;

  const { data, isLoading } = useQuery({
    queryKey: ['crm', 'due-lead-ids', window] as const,
    queryFn: () => {
      const now = new Date();
      const qs = new URLSearchParams();
      qs.set('status', 'scheduled');
      qs.set('sortBy', 'scheduledAt');
      qs.set('sortOrder', 'asc');
      qs.set('limit', '100');
      if (window === 'today') {
        qs.set('scheduledFrom', startOfDayInOrgTz(now).toISOString());
        qs.set('scheduledTo', endOfDayInOrgTz(now).toISOString());
      } else {
        qs.set('scheduledTo', now.toISOString());
      }
      return fetchAppointments(qs);
    },
    enabled,
    staleTime: 15_000,
    // "Atrasado" e "hoje" mudam com o relógio — refetch periódico (não é
    // um polling agressivo, só evita que o filtro rápido fique preso ao
    // instante em que a tela abriu).
    refetchInterval: 60_000,
  });

  const leadIds = new Set<string>();
  if (data) {
    for (const appointment of data.data) {
      if (window === 'overdue' && !appointment.isOverdue) continue;
      leadIds.add(appointment.leadId);
    }
  }

  return { leadIds, isLoading: enabled && isLoading };
}
